import { getFormattedValues } from './get-formatted-values.js';
import type { ChartUnit } from '../../../../core/types/chart-unit.js';
import type { DataPoint } from '../../../../core/types/data-point.js';
import type { Formatter } from '../../../../core/types/formatter.js';
import type { TooltipItem } from '../../../../core/types/tooltip.js';
import { safeGet } from '../../../providers/HistogramDataProvider.js';
import type { HistogramBin, WeakMapBins } from '../../../types/histogram.js';

export interface HistogramTooltipDataPoint extends DataPoint {
  bin: HistogramBin;
}

type AsyncValuesTooltipItem = TooltipItem & { asyncValues: () => string[] };

/**
 * generate the tooltip items from the points and closes point
 *
 * return -
 */

export function toTooltipItems(
  binsMap: WeakMapBins,
  formatters: Formatter[],
  isSingle: boolean,
  units: ChartUnit[],
  points?: HistogramTooltipDataPoint[],
  closestPoint?: HistogramTooltipDataPoint,
): {
  tooltipItems: AsyncValuesTooltipItem[];
  closestTooltipItem?: AsyncValuesTooltipItem;
} {
  if (!closestPoint) {
    return { tooltipItems: [], closestTooltipItem: undefined };
  }

  const binMetadata = safeGet(binsMap, closestPoint.bin);
  const [leftUnit] = units;
  const [leftFormatter, rightFormatter] = formatters;
  const formatter =
    binMetadata.unit === leftUnit ? leftFormatter : rightFormatter;

  const closestTooltipItem = {
    id: binMetadata.seriesId,
    name: binMetadata.seriesName,
    color: binMetadata.color,
    asyncValues: () => getFormattedValues(formatter, binMetadata),
    bin: closestPoint.bin,
  };

  if (isSingle) {
    return {
      tooltipItems: [closestTooltipItem],
      closestTooltipItem,
    };
  }

  if (!points) {
    return { tooltipItems: [], closestTooltipItem };
  }

  const tooltipItems = points
    .sort((a, b) => a.y - b.y)
    .map((point) => {
      const binSharedMetadata = safeGet(binsMap, point.bin);

      return {
        id: binSharedMetadata.seriesId,
        name: binSharedMetadata.seriesName,
        color: binSharedMetadata.color,
        asyncValues: () => getFormattedValues(formatter, binSharedMetadata),
        bin: point.bin,
      };
    });

  return { tooltipItems, closestTooltipItem };
}
